/**
 * Pointer drag that stays attached on mouse, pen, and touch.
 *
 * The 3D canvas (OrbitControls) and the browser's pan/zoom gestures will
 * otherwise steal the pointer as soon as the contact leaves the small handle,
 * which is why panels stop following a finger.
 */

const LISTENER_OPTS = { capture: true, passive: false };

let dragDepth = 0;

function beginDragLock() {
  dragDepth += 1;
  document.body.classList.add('mdv-dragging');
}

function endDragLock() {
  dragDepth = Math.max(0, dragDepth - 1);
  if (dragDepth === 0) document.body.classList.remove('mdv-dragging');
}

function capturePointer(handle, pointerId) {
  if (!handle || typeof handle.setPointerCapture !== 'function') return;
  try {
    handle.setPointerCapture(pointerId);
  } catch {
    /* pointer may already have been released */
  }
}

/**
 * @param {PointerEvent} event
 * @param {{
 *   onMove?: (ev: PointerEvent) => void,
 *   onEnd?: (ev: PointerEvent) => void,
 * }} handlers
 * @returns {(() => void) | null} abort function, or null if this pointer was ignored
 */
export function startPointerDrag(event, { onMove, onEnd } = {}) {
  if (event.button != null && event.button !== 0) return null;

  const pointerId = event.pointerId;
  const handle = event.currentTarget;
  if (event.cancelable) event.preventDefault();
  event.stopPropagation();

  if (handle) {
    handle.style.touchAction = 'none';
    capturePointer(handle, pointerId);
  }

  beginDragLock();

  let ended = false;
  let frame = 0;
  let latest = null;

  const flushMove = () => {
    frame = 0;
    if (ended || !latest) return;
    onMove?.(latest);
  };

  const onPointerMove = (ev) => {
    if (ev.pointerId !== pointerId) return;
    if (ev.cancelable) ev.preventDefault();
    latest = ev;
    if (!frame) frame = requestAnimationFrame(flushMove);
  };

  const recapture = (ev) => {
    if (ev.pointerId !== pointerId || ended) return;
    capturePointer(handle, pointerId);
  };

  const detach = () => {
    window.removeEventListener('pointermove', onPointerMove, LISTENER_OPTS);
    window.removeEventListener('pointerup', finish, LISTENER_OPTS);
    window.removeEventListener('pointercancel', finish, LISTENER_OPTS);
    if (handle) {
      handle.removeEventListener('lostpointercapture', recapture);
      if (typeof handle.hasPointerCapture === 'function' && handle.hasPointerCapture(pointerId)) {
        try {
          handle.releasePointerCapture(pointerId);
        } catch {
          /* already released */
        }
      }
    }
    endDragLock();
  };

  const finish = (ev) => {
    if (ev && ev.pointerId !== pointerId) return;
    if (ended) return;
    ended = true;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
    if (latest) onMove?.(latest);
    detach();
    onEnd?.(ev);
  };

  const abort = () => {
    if (ended) return;
    ended = true;
    if (frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
    detach();
  };

  window.addEventListener('pointermove', onPointerMove, LISTENER_OPTS);
  window.addEventListener('pointerup', finish, LISTENER_OPTS);
  window.addEventListener('pointercancel', finish, LISTENER_OPTS);
  if (handle) handle.addEventListener('lostpointercapture', recapture);

  return abort;
}
